import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { useFiltersContext } from "./Filters";

import {
    OpenGamesContextProps,
    OpenGamesContextValues,
} from "../types/contexts";
import { SortColumns } from "../types/opengames";
import { defaultDateFormat } from "../../../lib/utils";
import { IGame } from "../../../types/api";
import { SortOrders } from "../../../types/shared";

const OpenGamesContext = createContext<OpenGamesContextValues | null>(null);

export const useOpenGamesContext = () => {
    const context = useContext(OpenGamesContext);
    if (!context) {
        throw new Error(
            "useOpenGamesContext must be used within a OpenGamesProvider",
        );
    }
    return context;
};

export const OpenGamesProvider: React.FC<OpenGamesContextProps> = props => {
    const { children, initialGames, umpirePay, site } = props;
    const { filters } = useFiltersContext();
    const [games, setGames] = useState<IGame[]>([]);

    useEffect(() => {
        // Filter out full games here so that the filters populate properly
        // This will set the filter options to only the open games with those options

        const filteredGames = initialGames.filter(g => {
            const pay = umpirePay?.find(
                u => g.ageLevel.ageLevel === u.ageLevel,
            ) || { umpires: [] };

            return (
                !g.umpires?.length ||
                g.umpires?.some(
                    u => u.replacement === 1 && !u.pendingReplacement,
                ) ||
                (pay?.umpires?.length === 2
                    ? !g.umpires.find(u => u.location === "plate") ||
                      !g.umpires.find(u => u.location === "field")
                    : !g.umpires.find(u => u.location === "plate"))
            );
        });
        setGames(filteredGames);
    }, [initialGames, umpirePay]);

    const [sortColumn, setSortColumn] = useState<SortColumns>("date");
    const [sortOrder, setSortOrder] = useState<SortOrders>("ascending");

    const filteredGames = useMemo(() => {
        return games
            .filter(g =>
                filters.ageLevels.filter
                    ? filters.ageLevels.filter?.includes(g.ageLevel.ageLevel)
                    : true,
            )
            .filter(g => {
                return filters.dateStart
                    ? new Date(defaultDateFormat.format(new Date(g.date))) >=
                          new Date(defaultDateFormat.format(filters.dateStart))
                    : true;
            })
            .filter(g => {
                return filters.dateEnd
                    ? new Date(defaultDateFormat.format(new Date(g.date))) <=
                          new Date(defaultDateFormat.format(filters.dateEnd))
                    : true;
            })
            .filter(g =>
                filters.location.filter
                    ? filters.location.filter?.includes(g.fieldName)
                    : true,
            );
    }, [games, filters, umpirePay]);

    const sortedGames = useMemo(() => {
        let aValue: any;
        let bValue: any;

        return [...filteredGames].sort((a, b) => {
            switch (sortColumn) {
                case "age":
                    aValue = parseInt(a.ageLevel.ageLevel);
                    bValue = parseInt(b.ageLevel.ageLevel);
                    break;
                case "location":
                    aValue = a.fieldName;
                    bValue = b.fieldName;
                    break;
                case "date":
                    aValue = new Date(a.date).getTime();
                    bValue = new Date(b.date).getTime();
                    break;
            }

            if (sortOrder === "ascending") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }, [filteredGames, sortColumn, sortOrder]);

    const contextValue = useMemo(
        () => ({
            games: sortedGames,
            setGames,
            totalItems: sortedGames.length,
            sortColumn,
            setSortColumn,
            sortOrder,
            setSortOrder,
            umpirePay,
            site,
        }),
        [sortedGames],
    );

    return (
        <OpenGamesContext.Provider value={contextValue}>
            {children}
        </OpenGamesContext.Provider>
    );
};
